/**
 * Blog content store.
 *
 * Posts are TypeScript modules so they ship with the bundle and don't need
 * a CMS. Add a new entry to POSTS and a route automatically picks it up.
 */

export type Post = {
  slug: string;
  title: string;
  description: string;
  author: string;
  publishedAt: string; // ISO date
  readingMinutes: number;
  tags: string[];
  /**
   * Article body as a sequence of blocks. Keep it markup-free so the
   * renderer (`<BlogArticle />`) controls typography.
   */
  body: Block[];
};

export type Block =
  | { kind: "p"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "quote"; text: string; cite?: string }
  | { kind: "callout"; title: string; text: string }
  | { kind: "cta"; text: string; href: string; label: string };

const AUTHOR = "Tilt University Editorial";

export const POSTS: Post[] = [
  {
    slug: "how-to-outline-a-five-paragraph-essay",
    title: "How to Outline a Five-Paragraph Essay (with Examples)",
    description:
      "A teacher-built walkthrough of the five-paragraph essay outline — thesis, three body paragraphs, and a conclusion that actually lands — with two full examples you can copy.",
    author: AUTHOR,
    publishedAt: "2026-06-10",
    readingMinutes: 8,
    tags: ["essay outline", "five-paragraph essay", "writing"],
    body: [
      { kind: "p", text: "The five-paragraph essay gets a bad reputation, but it is still the fastest way to teach the one skill that separates a B paper from an A paper: making a claim and supporting it with evidence. Before you can write a great essay, you have to be able to write a clean one. This guide walks you through the outline most teachers actually grade against, with two real examples." },
      { kind: "h2", text: "What goes in a five-paragraph essay outline" },
      { kind: "p", text: "Every five-paragraph essay has the same skeleton. The order is intentional — each part sets up the next." },
      { kind: "ol", items: [
        "Hook — one or two sentences that earn the reader's attention.",
        "Background — the context a reader needs to understand the thesis.",
        "Thesis — the single sentence your whole essay defends.",
        "Body paragraph 1 — your strongest reason, with evidence and analysis.",
        "Body paragraph 2 — your second reason, with evidence and analysis.",
        "Body paragraph 3 — your third reason, often a counter or nuance.",
        "Conclusion — restate the thesis in new words, then zoom out to the so-what.",
      ] },
      { kind: "callout", title: "The rule of three", text: "If a body paragraph has less than three sentences of your own analysis, the evidence is doing the work for you. Add a sentence that names what the quote shows, and another that connects it back to the thesis." },
      { kind: "h2", text: "Example outline: argumentative essay" },
      { kind: "h3", text: "Prompt: Should public libraries lend e-books?" },
      { kind: "p", text: "Thesis: Public libraries should expand e-book lending because it lowers the cost of access for low-income readers, meets students where they already read, and reduces the long-term cost of maintaining physical collections." },
      { kind: "ul", items: [
        "Body 1 (access) — quote a 2023 ALA report on rural patrons, then explain how device-based lending closes a literal distance gap.",
        "Body 2 (student behavior) — cite a Pew study on teen reading, then connect to library check-out data.",
        "Body 3 (cost) — quote a city budget memo on collection upkeep, then weigh against e-book licensing fees.",
      ] },
      { kind: "h2", text: "Example outline: literary analysis" },
      { kind: "h3", text: "Prompt: How does The Great Gatsby use color?" },
      { kind: "p", text: "Thesis: Fitzgerald uses color in The Great Gatsby — green, white, and yellow in particular — to externalize the gap between what his characters claim to want and what they actually pursue." },
      { kind: "ul", items: [
        "Body 1 (green) — open with the green light at Daisy's dock; analyze how Gatsby's longing is staged as distance.",
        "Body 2 (white) — quote Daisy and Jordan's first appearance; show how purity is a costume.",
        "Body 3 (yellow) — Gatsby's car and Myrtle's death; argue that gold curdles into yellow when wealth meets consequence.",
      ] },
      { kind: "h2", text: "Mistakes to avoid" },
      { kind: "ul", items: [
        "Writing a thesis that lists topics instead of stating a claim ('I will talk about X, Y, and Z').",
        "Stacking quotes without analysis — every quote needs at least two sentences from you.",
        "Restating the thesis word-for-word in the conclusion. Use new language; reach for a bigger implication.",
      ] },
      { kind: "cta", text: "Skip the blank page and generate a framed five-paragraph outline with real cited quotes in under a minute.", href: "/generator", label: "Generate my outline" },
    ],
  },
  {
    slug: "mla-vs-apa-7-which-citation-style",
    title: "MLA vs APA 7: Which Citation Style Does Your Class Use?",
    description:
      "A side-by-side guide to MLA 9 and APA 7 — when each is used, what the citations look like, and the small formatting rules that trip up the most students.",
    author: AUTHOR,
    publishedAt: "2026-06-11",
    readingMinutes: 7,
    tags: ["MLA", "APA", "citations"],
    body: [
      { kind: "p", text: "Half the grade-killing mistakes on student essays are not bad ideas — they are wrong citations. Different classes want different styles, and the differences are small but specific. This guide shows you exactly which style your class probably uses and how to format the two most common citation types correctly the first time." },
      { kind: "h2", text: "Which classes use which style?" },
      { kind: "ul", items: [
        "MLA 9 — English, literature, foreign languages, most humanities.",
        "APA 7 — psychology, education, sociology, nursing, and most social sciences.",
        "Chicago / Turabian — history and some art history classes (not covered here).",
      ] },
      { kind: "p", text: "If your syllabus or rubric does not say, the safest bet is to match what your textbook uses. When in doubt, email your teacher — it is the cheapest grade you'll ever protect." },
      { kind: "h2", text: "In-text citations side-by-side" },
      { kind: "h3", text: "Quoting a book" },
      { kind: "quote", text: "MLA: \"So we beat on, boats against the current\" (Fitzgerald 180).", cite: "Author Page format" },
      { kind: "quote", text: "APA: \"So we beat on, boats against the current\" (Fitzgerald, 1925, p. 180).", cite: "Author, Year, p. Page format" },
      { kind: "h3", text: "Paraphrasing an article" },
      { kind: "quote", text: "MLA: Recent research suggests teens read more on phones than on paper (Anderson 12).", cite: "" },
      { kind: "quote", text: "APA: Recent research suggests teens read more on phones than on paper (Anderson, 2024).", cite: "" },
      { kind: "h2", text: "Works Cited vs References" },
      { kind: "p", text: "Both styles put a list of sources at the end of the paper, but the heading, the order of fields, and even the capitalization rules differ." },
      { kind: "ul", items: [
        "MLA: heading is 'Works Cited'. Author last, first. Title in title case. Italicize books and journals.",
        "APA: heading is 'References'. Author last, F. Year in parentheses after author. Sentence case for article titles, title case for journal names.",
      ] },
      { kind: "callout", title: "The italics rule that actually matters", text: "Both styles italicize the title of the larger work (book, journal, film) and use quotation marks around the smaller work inside it (chapter, article, song). Get this one rule right and you'll pass 90 percent of citation checks." },
      { kind: "h2", text: "Formatting checks teachers run first" },
      { kind: "ol", items: [
        "Double-spaced, 12pt Times New Roman or 11pt Calibri.",
        "1-inch margins, page numbers in the top right.",
        "MLA: header with last name + page number. APA 7: running head only on student papers if the instructor asks.",
        "Hanging indent on every Works Cited / References entry.",
      ] },
      { kind: "cta", text: "Generate a draft with properly formatted MLA or APA 7 citations baked in — no manual fixing.", href: "/generator", label: "Try the generator" },
    ],
  },
  {
    slug: "find-real-quotes-without-chatgpt-hallucinations",
    title: "How to Find Real Quotes for Your Essay (Without ChatGPT Making Them Up)",
    description:
      "Why ChatGPT invents quotes and citations, how teachers spot it, and the safer workflow students should use to find real, verifiable evidence for any essay.",
    author: AUTHOR,
    publishedAt: "2026-06-12",
    readingMinutes: 6,
    tags: ["AI", "citations", "academic integrity"],
    body: [
      { kind: "p", text: "If you've ever asked ChatGPT for a quote and watched your teacher fail you for it, you already know the problem. General-purpose chatbots are wired to sound helpful, so when they can't find a real quote they invent one — confidently, with a page number and an author name. The technical word for this is 'hallucination.' The grading word for it is 'zero.'" },
      { kind: "h2", text: "Why ChatGPT invents quotes" },
      { kind: "p", text: "Language models predict the next word that sounds right. When you ask for a quote from a book they don't have on hand, the model doesn't say 'I don't know' — it predicts what a quote from that book would look like. The result reads like a real quote because, statistically, it is shaped like one. It just doesn't exist." },
      { kind: "callout", title: "How teachers catch it", text: "Most teachers Google the quote inside double quotation marks. If it doesn't appear anywhere on the open web, the quote is fabricated. This takes about ten seconds. They will also notice when the page number doesn't match the edition the class is using." },
      { kind: "h2", text: "A safer workflow for finding real quotes" },
      { kind: "ol", items: [
        "Start with the actual text. Open the book, article, or PDF and search (Cmd/Ctrl-F) for a keyword from your claim.",
        "Read 20 lines around the keyword. The strongest quotes are usually one sentence before or after where the keyword appears.",
        "Copy the quote word-for-word. Note the page number from the edition you are using — not from Google Books.",
        "Cite it in your essay's style (MLA or APA 7).",
      ] },
      { kind: "h2", text: "When you can use AI honestly" },
      { kind: "p", text: "AI is genuinely good at the surrounding work: organizing an outline, suggesting where evidence belongs, paraphrasing your own sentences for clarity, and formatting citations once you have the real source. Where it fails is making up the source itself." },
      { kind: "p", text: "The tools that are safe for essay writing pull from real text you give them — your source PDF, an article you uploaded, a passage you pasted. They quote from that text, with a real page or paragraph number. If a tool produces a 'quote' without you giving it any source, treat the quote as suspect until you can verify it." },
      { kind: "h2", text: "A two-minute verification habit" },
      { kind: "ol", items: [
        "Copy the quote with the quotation marks.",
        "Paste into Google. Wrap it in double quotes so Google does an exact match.",
        "If nothing comes up, the quote is probably fake. Remove it or find a real one.",
      ] },
      { kind: "cta", text: "SmartEssay only quotes from text you give it — every quote is real, with the page number where it appears.", href: "/generator", label: "Generate a sourced outline" },
    ],
  },
];

export function getPost(slug: string): Post | undefined {
  return POSTS.find((p) => p.slug === slug);
}
